import {
  Node,
  PasteRuleMatch,
  mergeAttributes,
  nodePasteRule,
} from '@tiptap/core'
import { TextSelection } from '@tiptap/pm/state'
import { ReactNodeViewRenderer } from '@tiptap/react'
import { find } from 'linkifyjs'
import { opalObjectByUrl } from '@opallabs/routing'
import { NodeView as EmbeddedResourceNodeView } from './NodeView'

export interface EmbeddedResourceOptions {
  /**
   * HTML attributes to add to the image element.
   * @default {}
   * @example { class: 'foo' }
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  HTMLAttributes: Record<string, any>
}

export interface EmbeddedResourceStorage {
  /**
   * Embed data is loaded in from the server, but we can't
   * fetch it until the rich-texts service saves the document.
   * Setting `shouldRefetch` to true will trigger the fetch process
   * the next time the document attempts to save.
   * See `debouncedSave` in <TextEditor> for more detail.
   */
  shouldRefetch: boolean
  /**
   * Object ids of embeds added in this editor session whose resource has not
   * shown up in the rich-text document yet, keyed to when they were added.
   * The node views poll the document only for these ids.
   */
  pendingObjectIds: Map<string, number>
}

export type EmbeddedResourceAttributes = {
  embedType: 'inline' | 'card'
  href: string
  objectId: string
  objectType: string
  label?: string
  target?: string | null
  rel?: string | null
  class?: string | null
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    embeddedResource: {
      convertEmbeddedResourceToLink: (
        attributes: EmbeddedResourceAttributes
      ) => ReturnType

      convertLinkToEmbeddedResource: (
        attributes: EmbeddedResourceAttributes
      ) => ReturnType

      /**
       * Set an embedded resource
       * @param attributes Embedded Resource attributes
       */
      setEmbeddedResource: (
        attributes: EmbeddedResourceAttributes
      ) => ReturnType

      /**
       * Set an embedded resource
       * @param attributes Embedded Resource attributes
       */
      unsetEmbeddedResource: (
        attributes: EmbeddedResourceAttributes
      ) => ReturnType
    }
  }

  interface Storage {
    embeddedResource: EmbeddedResourceStorage
  }
}

export const EmbeddedResource = Node.create<
  EmbeddedResourceOptions,
  EmbeddedResourceStorage
>({
  name: 'embeddedResource',
  group: 'inline',
  inline: true,
  marks: '',
  selectable: true,
  draggable: true,

  addAttributes() {
    return {
      embedType: {
        class: {
          default: this.options.HTMLAttributes.class,
        },
        default: 'inline',
        parseHTML: (element) => element.getAttribute('data-embed-type'),
        renderHTML: (attributes) => ({
          'data-embed-type': attributes.embedType,
        }),
      },
      href: {
        default: null,
      },
      label: {
        parseHTML: (element) => element.getAttribute('data-label'),
        renderHTML: (attributes) => ({ 'data-label': attributes.label }),
      },
      objectId: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-object-id'),
        renderHTML: (attributes) => ({ 'data-object-id': attributes.objectId }),
      },
      objectType: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-object-type'),
        renderHTML: (attributes) => ({
          'data-object-type': attributes.objectType,
        }),
      },
      rel: {
        default: this.options.HTMLAttributes.rel,
      },
      target: {
        default: this.options.HTMLAttributes.target,
      },
    }
  },

  addCommands() {
    return {
      convertEmbeddedResourceToLink:
        (attributes) =>
        ({ chain }) => {
          return (
            chain()
              .deleteNode(this.type)
              // Insert and Select link text
              .command(({ tr }) => {
                const textToInsert =
                  attributes.label && attributes.label.length
                    ? attributes.label
                    : attributes.href

                // Insert text at cursor
                tr.insertText(
                  textToInsert,
                  tr.mapping.map(tr.selection.$from.pos),
                  tr.mapping.map(tr.selection.$to.pos)
                )

                // Get selection for newly inserted text
                const [from, to] = [
                  tr.mapping.map(tr.selection.$from.pos),
                  tr.mapping.map(tr.selection.$to.pos) + textToInsert.length,
                ]

                // Select the text we just inserted
                tr.setSelection(TextSelection.create(tr.doc, from, to))

                return true
              })
              .setLink(attributes)
              .extendMarkRange('link')
              .run()
          )
        },
      convertLinkToEmbeddedResource:
        (attributes) =>
        ({ chain }) => {
          return chain()
            .extendMarkRange('link')
            .command(({ tr }) => {
              const from = tr.mapping.map(tr.selection.$from.pos)
              const to = tr.mapping.map(tr.selection.$to.pos)
              tr.setMeta('markText', tr.doc.textBetween(from, to))
              return true
            })
            .deleteSelection()
            .command(({ tr, commands }) => {
              const label = attributes.label ?? tr.getMeta('markText')

              return commands.setEmbeddedResource({
                ...attributes,
                label: label,
              })
            })
            .run()
        },
      setEmbeddedResource:
        (attributes) =>
        ({ commands }) => {
          // Before inserting: the node view can mount synchronously and reads
          // this on its first render.
          this.editor.storage.embeddedResource.pendingObjectIds.set(
            attributes.objectId,
            Date.now()
          )

          const insertSuccessful = commands.insertContent(
            {
              type: this.name,
              attrs: attributes,
            },
            { updateSelection: true }
          )

          if (insertSuccessful) {
            this.editor.storage.embeddedResource.shouldRefetch = true
          }

          return insertSuccessful
        },

      unsetEmbeddedResource:
        (attributes) =>
        ({ chain }) => {
          return (
            chain()
              .deleteNode(this.type)
              // Insert and Select link text
              .command(({ tr }) => {
                const textToInsert = attributes.label ?? attributes.href

                // Insert text at cursor
                tr.insertText(
                  textToInsert,
                  tr.mapping.map(tr.selection.$from.pos),
                  tr.mapping.map(tr.selection.$to.pos)
                )

                // Get selection for newly inserted text
                const [from, to] = [
                  tr.mapping.map(tr.selection.$from.pos),
                  tr.mapping.map(tr.selection.$to.pos) + textToInsert.length,
                ]

                // Select the text we just inserted
                tr.setSelection(TextSelection.create(tr.doc, from, to))

                return true
              })
              .run()
          )
        },
    }
  },

  addNodeView() {
    return ReactNodeViewRenderer(EmbeddedResourceNodeView, {
      contentDOMElementTag: 'span',
    })
  },

  addOptions() {
    return {
      HTMLAttributes: {
        target: '_blank',
        rel: 'noopener noreferrer nofollow',
        class: null,
      },
    }
  },

  addPasteRules() {
    return [
      nodePasteRule({
        getAttributes: (match) => {
          return {
            embedType: match.data?.embedType,
            href: match.data?.href,
            label: match.data?.label,
            objectId: match.data?.objectId,
            objectType: match.data?.objectType,
          } satisfies EmbeddedResourceAttributes
        },
        find: (text) => {
          const foundLinks: PasteRuleMatch[] = []

          const links = find(text).filter((item) => {
            if (!item.isLink) {
              return false
            }

            try {
              // Attempting to construct invalid URLs will throw an exception
              return opalObjectByUrl(new URL(item.value).pathname) !== null
            } catch {
              return false
            }
          })

          if (links.length) {
            links.forEach((link) => {
              const opalLink = opalObjectByUrl(new URL(link.value).pathname)
              if (!opalLink) return

              this.editor.storage.embeddedResource.pendingObjectIds.set(
                opalLink.id,
                Date.now()
              )

              foundLinks.push({
                index: link.start,
                text: link.value,
                data: {
                  embedType: opalLink.type === 'content' ? 'card' : 'inline',
                  href: link.href,
                  objectId: opalLink.id,
                  objectType: opalLink.type,
                } satisfies EmbeddedResourceAttributes,
              })
            })
          }

          if (foundLinks.length > 0) {
            this.editor.storage.embeddedResource.shouldRefetch = true
          }

          return foundLinks
        },
        type: this.type,
      }),
    ]
  },

  addStorage() {
    return {
      shouldRefetch: false,
      pendingObjectIds: new Map(),
    }
  },

  renderText({ node }) {
    return node.attrs.href
  },

  parseHTML() {
    return [{ tag: 'embedded-resource-component' }]
  },

  renderHTML({ HTMLAttributes }) {
    return ['embedded-resource-component', mergeAttributes(HTMLAttributes)]
  },
})
